import React, { useEffect, useState } from 'react';

// LIBS
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';

// SERVICES
import { convertToDate } from 'js/library/utils/helpers';
import { cardTypes } from './lists';
import {
  detectCardType,
  downloadCardImages,
  fetchKeywords,
  fetchOrganizations,
  reorganizeSystemQueryGroups,
  validateSystemQuery,
} from './cardServices';
import { updateCardsStoreAction } from './cardApiService';

// STYLES
import { withStyles } from '@mui/styles';
import { TextField, MenuItem } from '@mui/material';

// COMPONENTS
import View from './View';

let orgsSuggestions = [];

const styles = () => ({
  root: {
    background: '#ffffff',
    borderRadius: 5,
    border: '1px solid #6c6c6c',
    color: 'white',
  },
});

function renderInput(inputProps) {
  const { InputProps, classes, ref, ...other } = inputProps;

  return (
    <TextField
      InputProps={{
        inputRef: ref,
        classes: {
          root: classes.inputRoot,
          input: classes.inputInput,
        },
        ...InputProps,
      }}
      {...other}
    />
  );
}

const SetCards = (props) => {
  const infosForEdit = props.location.state ?? [];

  const history = useHistory();
  const url = window.location.pathname;
  const isCreatingCard = url.includes('cadastrarCards');

  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(() => {
    // Determina de onde pegar o orderType baseado no tipo do card
    const getOrderType = () => {
      if (infosForEdit?.type === 'card_list') {
        return infosForEdit?.storePlacesQuery?.orderType ?? 'relevance';
      }
      return infosForEdit?.orderType ?? 'relevance';
    };

    return {
      cardId: infosForEdit?.cardId ?? null,
      cardType: infosForEdit?.type ?? 'card_list',
      title: infosForEdit?.title ?? '',
      subtitle: infosForEdit?.subtitle ?? '',
      description: infosForEdit?.description ?? '',
      orderType: getOrderType(),
      hasVoucher: infosForEdit?.hasVoucher ?? false,
      hasPromo: infosForEdit?.hasPromo ?? false,
      geoRadius: infosForEdit?.geoRadius ?? 1000,
      platform: infosForEdit?.platform ?? ['Web'],
      priority: infosForEdit?.priority ?? 1,
      action: infosForEdit?.action ?? '',
      isPublished: infosForEdit?.isPublished ?? false,
      status: infosForEdit?.status ?? 1,
      keywords: '',

      icon: infosForEdit?.images?.icon ?? '',
      imageWeb: infosForEdit?.images?.web ?? '',
      imageMobile: infosForEdit?.images?.mobile ?? '',
      imageMobileDetails: infosForEdit?.images?.mobileDetails ?? '',

      itemId: infosForEdit?.appFilter?.itemId ?? '',
      query: infosForEdit?.appFilter?.query ?? '',
      url: infosForEdit?.appFilter?.url ?? '',

      enableDate: convertToDate(infosForEdit?.enableDate) ?? '',
      disableDate: convertToDate(infosForEdit?.disableDate) ?? '',
      createDate: convertToDate(infosForEdit?.createDate) ?? '',

      location: {
        latitude: infosForEdit?.location?.latitude ?? 0,
        longitude: infosForEdit?.location?.longitude ?? 0,
        radius: infosForEdit?.location?.radius ?? 0,
      },

      organizations: '',
      orgIDList: infosForEdit?.orgs ? [...infosForEdit.orgs.map((orgID) => orgID)] : [],
      organizationsList: [],
      orgs: [],

      loadedWebFile: null,
      loadedMobileFile: null,
      loadedDetailsFile: null,

      keywordsList: infosForEdit?.keyWordsSet ?? [],
      storePlacesQuery: infosForEdit?.storePlacesQuery ?? {},
    };
  });

  const [selectedCardType, setSelectedCardType] = useState([]);
  const [groups, setGroups] = useState({});
  const [returnType, setReturnType] = useState(['physical']);
  const [suggestions, setSuggestions] = useState([]);

  const [selectedEstablishment, setSelectedEstablishment] = useState(null);
  const [selectedStore, setSelectedStore] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // state que guarda o id do estabelecimento/promoção/produto que virá selecionado na edição
  const [selectedIdEdit, setSelectedIdEdit] = useState('');
  const [invalidFields, setInvalidFields] = useState({});

  const [cardDefaultWeb, setCardDefaultWeb] = useState('');
  const [cardDefaultMobile, setCardDefaultMobile] = useState('');
  const [cardDefaultDetails, setCardDefaultDetails] = useState('');

  // atualiza o systemQuery do form sempre que os grupos mudarem
  useEffect(() => {
    setForm((prevState) => ({
      ...prevState,
      storePlacesQuery: {
        ...prevState.storePlacesQuery,
        systemQuery: {
          ...groups,
        },
      },
    }));
  }, [groups]);

  // edição: para o tipo de retorno
  useEffect(() => {
    if (infosForEdit?.storePlacesQuery) {
      // Determina qual tipo está presente no storePlacesQuery
      const typeKey = Object.keys(infosForEdit.storePlacesQuery).find((key) =>
        ['promotionType', 'establishmentType', 'productType'].includes(key)
      );

      if (typeKey) {
        // Garante que o valor é sempre um array
        const types = Array.isArray(infosForEdit.storePlacesQuery[typeKey])
          ? infosForEdit.storePlacesQuery[typeKey]
          : [infosForEdit.storePlacesQuery[typeKey]];

        setReturnType(types);
      }
    }
  }, [infosForEdit]);

  // edição: seta o systemQuery no state groups e seta o id da promo ou estab
  useEffect(() => {
    if (infosForEdit?.storePlacesQuery?.systemQuery) {
      const { systemQuery } = infosForEdit.storePlacesQuery;

      setGroups(systemQuery || {});

      Object.values(systemQuery).forEach((group) => {
        if (Array.isArray(group)) {
          group.forEach((condition) => {
            if (
              condition &&
              typeof condition === 'object' &&
              condition.field &&
              condition.value !== undefined
            ) {
              const { field, value } = condition;

              if (field === 'establishmentId' || field === 'id') {
                setSelectedIdEdit(value);
              }
            }
          });
        }
      });
    }
  }, [infosForEdit?.storePlacesQuery?.systemQuery]);

  // edição: preenche o campo tipo de card com o valor correto ao abrir a edição
  useEffect(() => {
    const detectedType = detectCardType(infosForEdit.storePlacesQuery);
    if (detectedType) {
      setSelectedCardType(cardTypes.find((card) => card.action === detectedType));
    }
  }, [infosForEdit]);

  // keywords, organizações e downloadImages
  useEffect(() => {
    fetchKeywords().then(setSuggestions);
    fetchOrganizations(orgsSuggestions, form, setForm);

    downloadCardImages(
      infosForEdit,
      form,
      setCardDefaultWeb,
      setCardDefaultMobile,
      setCardDefaultDetails
    );
  }, []);

  // para isPublished, hasPromo e hasVoucher
  const handleChangeSwitch = (name) => (event) => {
    if (name === 'isPublished') {
      setForm((prevForm) => ({
        ...prevForm,
        [name]: event.target.checked,
        goBack: true,
      }));
    } else {
      setForm((prevForm) => ({
        ...prevForm,
        storePlacesQuery: {
          ...prevForm.storePlacesQuery,
          [name]: event.target.checked,
        },
      }));
    }
  };

  const handleChangeKeywordDownshift = (item) => {
    if (form.keywordsList.indexOf(item) === -1) {
      setForm((prevForm) => ({
        ...prevForm,
        keywords: '',
        keywordsList: [...form.keywordsList, item],
      }));
    }
  };

  const handleChangeOrganizationDownshift = (item) => {
    if (form.organizationsList.indexOf(item) === -1)
      setForm((prevForm) => ({
        ...prevForm,
        organizations: '',
        organizationsList: [...form.organizationsList, item],
      }));
  };

  const handleDeleteDownshift = (name, item) => () => {
    setForm((prevForm) => {
      const updatedKeywordsList = [...prevForm.keywordsList];
      const updatedOrganizationsList = [...prevForm.organizationsList];

      if (name === 'keywords') {
        updatedKeywordsList.splice(updatedKeywordsList.indexOf(item), 1);
        return { ...prevForm, keywordsList: updatedKeywordsList };
      }

      updatedOrganizationsList.splice(updatedOrganizationsList.indexOf(item), 1);
      return { ...prevForm, organizationsList: updatedOrganizationsList };
    });
  };

  //altera os states dos campos, caso o usuário insira informações nos input
  const handleChange = (name) => (event) => {
    if (name === 'enableDate' || name === 'disableDate') {
      setForm((prevForm) => ({
        ...prevForm,
        [name]: convertToDate(event.target.value),
      }));
    } else if (name === 'cardType') {
      const isCardList = event.target.value === 'card_list';

      setForm((prevForm) => {
        const newForm = {
          ...prevForm,
          [name]: event.target.value,
        };

        // Se mudou para card_list, move o orderType para storePlacesQuery
        if (isCardList) {
          newForm.storePlacesQuery = {
            ...prevForm.storePlacesQuery,
            orderType: prevForm.orderType || 'relevance',
          };
        } else {
          // Se mudou para outro tipo, move o orderType para a raiz
          newForm.orderType = prevForm.storePlacesQuery?.orderType || 'relevance';
        }

        return newForm;
      });
    } else if (name === 'orderType') {
      setForm((prevForm) => {
        const updatedForm = {
          ...prevForm,
          [name]: event.target.value,
        };

        // Atualiza no storePlacesQuery se for card_list
        if (prevForm.cardType === 'card_list') {
          updatedForm.storePlacesQuery = {
            ...prevForm.storePlacesQuery,
            orderType: event.target.value,
          };
        }

        return updatedForm;
      });
    } else {
      setForm((prevForm) => ({
        ...prevForm,
        [name]: event.target.value,
      }));
    }
  };

  // para plataformas
  const handleCheckArray = (name) => (event) => {
    const checked = event.target.checked;
    const value = event.target.value;
    setForm((prevForm) => {
      const aux = [...prevForm[name]];

      if (checked) {
        aux.push(value);
      } else {
        const index = aux.indexOf(value);
        if (index !== -1) {
          aux.splice(index, 1);
        }
      }

      return {
        ...prevForm,
        [name]: aux,
      };
    });
  };

  // criar / editar cards
  const handleSubmitCard = () => {
    const data = { ...form };
    data.orgs = form.organizationsList.map(({ orgID }) => orgID);

    if (cardDefaultWeb === '') {
      toast.warn('A imagem web é obrigatória!');
    } else {
      data.loadedWebFile =
        isCreatingCard || cardDefaultWeb.includes('base64') ? cardDefaultWeb : null;
    }

    if (cardDefaultMobile === '') {
      toast.warn('A imagem mobile é obrigatória!');
    } else {
      data.loadedMobileFile =
        isCreatingCard || cardDefaultMobile.includes('base64') ? cardDefaultMobile : null;
    }

    if (cardDefaultDetails === '') {
      toast.warn('A imagem detalhes é obrigatória!');
    } else {
      data.loadedDetailsFile =
        isCreatingCard || cardDefaultDetails.includes('base64') ? cardDefaultDetails : null;
    }

    if (form.cardType === 'card_list') {
      // Reorganiza os grupos do systemQuery antes de validar
      if (data.storePlacesQuery?.systemQuery) {
        data.storePlacesQuery.systemQuery = reorganizeSystemQueryGroups(
          data.storePlacesQuery.systemQuery
        );
      }

      // Validação do systemQuery
      const systemQueryErrors = validateSystemQuery(data.storePlacesQuery?.systemQuery);
      setInvalidFields(systemQueryErrors);

      // Verifica se há erros
      const hasQueryErrors = Object.keys(systemQueryErrors).some(
        (group) => Object.keys(systemQueryErrors[group]).length > 0
      );

      if (hasQueryErrors) {
        toast.warn('Por favor, preencha todos os valores das condições da query!');
        return;
      }
    }

    // Se todas as validações passarem
    if (cardDefaultWeb !== '' && cardDefaultMobile !== '' && cardDefaultDetails !== '') {
      setLoading(true);
      updateCardsStoreAction(data)
        .then(() => {
          if (isCreatingCard) {
            toast.success('Card criado com sucesso!');
          } else {
            toast.success('Card atualizado com sucesso!');
          }
          setTimeout(() => {
            history.push('/admin/configuracoes/cards');
          }, 2000);
        })
        .catch(() => {
          setLoading(false);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  function renderSuggestion({
    suggestion,
    index,
    itemProps,
    highlightedIndex,
    selectedItem,
    type,
  }) {
    const isHighlighted = highlightedIndex === index;
    const isSelected = (selectedItem || '').indexOf(suggestion.label) > -1;

    return (
      <MenuItem
        {...itemProps}
        key={suggestion.label}
        selected={isHighlighted}
        component="div"
        style={{
          fontWeight: isSelected ? 500 : 400,
        }}
      >
        {type === 'keywords' ? suggestion.label : suggestion.name}
      </MenuItem>
    );
  }

  renderSuggestion.propTypes = {
    highlightedIndex: PropTypes.number,
    index: PropTypes.number,
    itemProps: PropTypes.object,
    selectedItem: PropTypes.string,
    suggestion: PropTypes.shape({ label: PropTypes.string }).isRequired,
  };

  const { classes } = props;
  const {
    keywords,
    organizations,
    keywordsList: selectedKeywords,
    organizationsList: selectedOrgs,
  } = form;

  return (
    <>
      <View
        isCreatingCard={isCreatingCard}
        groups={groups}
        setGroups={setGroups}
        form={form}
        setForm={setForm}
        infosForEdit={infosForEdit}
        handleSubmitCard={handleSubmitCard}
        handleChange={handleChange}
        handleChangeSwitch={handleChangeSwitch}
        classes={classes}
        handleCheckArray={handleCheckArray}
        keywords={keywords}
        handleChangeKeywordDownshift={handleChangeKeywordDownshift}
        selectedKeywords={selectedKeywords}
        handleDeleteDownshift={handleDeleteDownshift}
        organizations={organizations}
        handleChangeOrganizationDownshift={handleChangeOrganizationDownshift}
        selectedOrgs={selectedOrgs}
        suggestions={suggestions}
        orgsSuggestions={orgsSuggestions}
        renderInput={renderInput}
        renderSuggestion={renderSuggestion}
        cardDefaultWeb={cardDefaultWeb}
        setCardDefaultWeb={setCardDefaultWeb}
        cardDefaultMobile={cardDefaultMobile}
        setCardDefaultMobile={setCardDefaultMobile}
        cardDefaultDetails={cardDefaultDetails}
        setCardDefaultDetails={setCardDefaultDetails}
        loading={loading}
        cardTypes={cardTypes}
        selectedCardType={selectedCardType}
        setSelectedCardType={setSelectedCardType}
        returnType={returnType}
        setReturnType={setReturnType}
        selectedEstablishment={selectedEstablishment}
        setSelectedEstablishment={setSelectedEstablishment}
        selectedStore={selectedStore}
        setSelectedStore={setSelectedStore}
        selectedProduct={selectedProduct}
        setSelectedProduct={setSelectedProduct}
        selectedIdEdit={selectedIdEdit}
        setSelectedIdEdit={setSelectedIdEdit}
        invalidFields={invalidFields}
        setInvalidFields={setInvalidFields}
      />
    </>
  );
};

export default withStyles(styles)(SetCards);
